import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { currencyService } from '../../services/currencyService';
import {
  adminSubscriptionService,
  type AdminSubscription,
} from '../../services/adminSubscriptionService';
import {
  adminMediaBuyingService,
  type MediaBuyingAccount,
  type MediaPauseEntry,
  type MediaSpendEntry,
} from '../../services/adminMediaBuyingService';

const monthKey = (value?: string) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 7);
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const emptySpend = (): MediaSpendEntry => ({
  id: `spend_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  date: todayKey(),
  amount: 0,
  campaignName: '',
  notes: '',
});

const emptyPause = (): MediaPauseEntry => ({
  id: `pause_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  startDate: todayKey(),
  endDate: todayKey(),
  resumeDate: todayKey(),
  reason: '',
  status: 'planned',
});

const MediaBuyingManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [accounts, setAccounts] = useState<MediaBuyingAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [googleAdsConfig, setGoogleAdsConfig] = useState({
    managerCustomerId: '',
    loginCustomerId: '',
    developerToken: '',
    oauthClientId: '',
    oauthClientSecret: '',
    refreshToken: '',
    connected: false,
    lastSyncAt: '',
    lastSyncStatus: '',
    lastSyncMessage: '',
  });
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [syncingGoogle, setSyncingGoogle] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [subs, media, googleConfigResponse] = await Promise.all([
          adminSubscriptionService.getSubscriptions(true),
          adminMediaBuyingService.getAccounts(),
          fetch('/api/admin/google-ads', { credentials: 'include', cache: 'no-store' }).then((response) =>
            response.json().catch(() => ({}))
          ),
        ]);
        setSubscriptions(subs);
        setAccounts(media);
        setSelectedAccountId(media[0]?.id || '');
        if (googleConfigResponse?.config) {
          setGoogleAdsConfig((current) => ({
            ...current,
            ...googleConfigResponse.config,
          }));
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erreur media buying');
      } finally {
        setLoading(false);
      }
    };
    load().catch(() => undefined);
  }, []);

  useEffect(() => currencyService.subscribe(setDisplayCountry), []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'google_ads_oauth_result') {
        return;
      }
      if (event.data.ok) {
        toast.success('Connexion Google Ads reussie');
      } else {
        toast.error(event.data.message || 'Connexion Google Ads echouee');
      }
      fetch('/api/admin/google-ads', { credentials: 'include', cache: 'no-store' })
        .then((response) => response.json().catch(() => ({})))
        .then((payload) => {
          if (payload?.config) {
            setGoogleAdsConfig((current) => ({ ...current, ...payload.config }));
          }
        })
        .catch(() => undefined);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const selectedAccount =
    accounts.find((entry) => entry.id === selectedAccountId) || null;

  const currentMonth = monthKey(new Date().toISOString());
  const remindersToReactivate = accounts.flatMap((account) =>
    account.pauses
      .filter((pause) => pause.status === 'planned' && pause.resumeDate === todayKey())
      .map((pause) => ({
        accountId: account.id,
        clientName: account.clientName,
        serviceName: account.serviceName,
        resumeDate: pause.resumeDate,
      }))
  );

  const metrics = useMemo(() => {
    let revenue = 0;
    let spend = 0;
    let target = 0;
    for (const account of accounts) {
      revenue += Number(account.monthlyRevenue || 0);
      target += Number(account.budgetTarget || 0);
      spend += account.spends
        .filter((entry) => monthKey(entry.date) === currentMonth)
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
    }
    return {
      revenue,
      spend,
      target,
      profit: revenue - spend,
    };
  }, [accounts, currentMonth]);

  const saveAll = async (nextAccounts: MediaBuyingAccount[]) => {
    setSaving(true);
    try {
      const saved = await adminMediaBuyingService.saveAccounts(nextAccounts);
      setAccounts(saved);
      if (!saved.find((entry) => entry.id === selectedAccountId)) {
        setSelectedAccountId(saved[0]?.id || '');
      }
      toast.success('Media buying sauvegarde');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sauvegarde impossible');
    } finally {
      setSaving(false);
    }
  };

  const saveGoogleAdsConfig = async () => {
    setSavingGoogle(true);
    try {
      const response = await fetch('/api/admin/google-ads', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          managerCustomerId: googleAdsConfig.managerCustomerId,
          loginCustomerId: googleAdsConfig.loginCustomerId,
          developerToken: googleAdsConfig.developerToken,
          oauthClientId: googleAdsConfig.oauthClientId,
          oauthClientSecret: googleAdsConfig.oauthClientSecret,
          refreshToken: googleAdsConfig.refreshToken,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Sauvegarde Google Ads impossible');
      }
      setGoogleAdsConfig((current) => ({
        ...current,
        ...(payload?.config || {}),
        developerToken: '',
        oauthClientSecret: '',
        refreshToken: '',
      }));
      toast.success('Connexion Google Ads agence sauvegardee');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur connexion Google Ads');
    } finally {
      setSavingGoogle(false);
    }
  };

  const syncGoogleAds = async () => {
    setSyncingGoogle(true);
    try {
      const response = await fetch('/api/admin/google-ads-sync', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Sync Google Ads impossible');
      }
      const refreshed = await adminMediaBuyingService.getAccounts();
      setAccounts(refreshed);
      setGoogleAdsConfig((current) => ({
        ...current,
        lastSyncAt: new Date().toISOString(),
        lastSyncStatus: 'success',
        lastSyncMessage: `Sync OK pour ${payload?.results?.length || 0} compte(s)`,
      }));
      toast.success(`Google Ads synchro OK (${payload?.results?.length || 0} compte(s))`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur sync Google Ads');
    } finally {
      setSyncingGoogle(false);
    }
  };

  const connectGoogleAdsOAuth = () => {
    const popup = window.open(
      '/api/admin/google-ads-auth-start',
      'googleAdsOAuth',
      'width=620,height=760,noopener,noreferrer'
    );
    if (!popup) {
      toast.error('Popup bloquee. Autorise les popups pour continuer.');
    }
  };

  const addFromSubscription = (subscription: AdminSubscription) => {
    const next: MediaBuyingAccount = {
      id: `media_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      clientName: subscription.clientName,
      clientEmail: subscription.clientEmail,
      subscriptionId: subscription.id,
      subscriptionNumber: subscription.subscriptionNumber,
      serviceName: subscription.serviceName,
      channel: 'google_ads',
      monthlyRevenue: Number(subscription.amount || 500),
      budgetTarget: 150,
      currencyCode: subscription.currencyCode || 'CAD',
      googleAdsCustomerId: '',
      spends: [],
      pauses: [],
      updatedAt: new Date().toISOString(),
    };
    const nextAccounts = [next, ...accounts];
    setAccounts(nextAccounts);
    setSelectedAccountId(next.id);
  };

  const patchSelected = (patch: Partial<MediaBuyingAccount>) => {
    if (!selectedAccount) return;
    setAccounts((current) =>
      current.map((entry) =>
        entry.id === selectedAccount.id
          ? { ...entry, ...patch, updatedAt: new Date().toISOString() }
          : entry
      )
    );
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-600">Chargement media buying...</div>;
  }

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-gray-900">Connexion Google Ads (compte agence)</h3>
        <p className="mt-2 text-sm text-gray-600">
          Connecte ton compte agence, puis on attribue les campagnes a chaque client via son Customer ID.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <input
            value={googleAdsConfig.managerCustomerId}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, managerCustomerId: event.target.value }))
            }
            placeholder="Manager Customer ID (MCC)"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            value={googleAdsConfig.loginCustomerId}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, loginCustomerId: event.target.value }))
            }
            placeholder="Login Customer ID (optionnel)"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            value={googleAdsConfig.oauthClientId}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, oauthClientId: event.target.value }))
            }
            placeholder="OAuth Client ID"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            value={googleAdsConfig.developerToken}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, developerToken: event.target.value }))
            }
            placeholder="Developer Token"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="password"
            value={googleAdsConfig.oauthClientSecret}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, oauthClientSecret: event.target.value }))
            }
            placeholder="OAuth Client Secret"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="password"
            value={googleAdsConfig.refreshToken}
            onChange={(event) =>
              setGoogleAdsConfig((current) => ({ ...current, refreshToken: event.target.value }))
            }
            placeholder="Refresh Token"
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={connectGoogleAdsOAuth}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Se connecter avec Google
          </button>
          <button
            type="button"
            onClick={saveGoogleAdsConfig}
            disabled={savingGoogle}
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-60"
          >
            {savingGoogle ? 'Sauvegarde...' : 'Sauvegarder connexion Google Ads'}
          </button>
          <button
            type="button"
            onClick={syncGoogleAds}
            disabled={syncingGoogle}
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60"
          >
            {syncingGoogle ? 'Sync...' : 'Sync campagnes Google Ads'}
          </button>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              googleAdsConfig.connected
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {googleAdsConfig.connected ? 'Connecte' : 'Configuration incomplete'}
          </span>
          {googleAdsConfig.lastSyncMessage && (
            <span className="text-xs text-gray-500">
              {googleAdsConfig.lastSyncMessage}
            </span>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Media Buying & budget pub</h2>
            <p className="mt-2 text-sm text-gray-600">
              Suis les depenses Google Ads, pauses pub, reactivations et marge reelle par client.
            </p>
          </div>
          <button
            type="button"
            onClick={() => saveAll(accounts)}
            disabled={saving}
            className="rounded-lg bg-neon-orange px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? 'Sauvegarde...' : 'Enregistrer media buying'}
          </button>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">CA offres pub</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {currencyService.formatCurrencyAmount(metrics.revenue, displayCurrencyCode, {
                country: displayCountry,
                display: 'code',
              })}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Depense pub (mois)</div>
            <div className="mt-1 text-2xl font-bold text-amber-700">
              {currencyService.formatCurrencyAmount(metrics.spend, displayCurrencyCode, {
                country: displayCountry,
                display: 'code',
              })}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Budget cible (mois)</div>
            <div className="mt-1 text-2xl font-bold text-indigo-700">
              {currencyService.formatCurrencyAmount(metrics.target, displayCurrencyCode, {
                country: displayCountry,
                display: 'code',
              })}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Benefice brut estime</div>
            <div className="mt-1 text-2xl font-bold text-emerald-700">
              {currencyService.formatCurrencyAmount(metrics.profit, displayCurrencyCode, {
                country: displayCountry,
                display: 'code',
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-gray-900">Rappels de reactivation aujourd hui</h3>
        <div className="mt-3 space-y-2">
          {remindersToReactivate.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune reactivation prevue aujourd hui.</p>
          ) : (
            remindersToReactivate.map((item) => (
              <div key={`${item.accountId}_${item.resumeDate}`} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                <span className="font-semibold">{item.clientName}</span> · {item.serviceName} · Reactiver la pub aujourd hui
              </div>
            ))
          )}
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <h3 className="text-lg font-semibold text-gray-900">Comptes pub client</h3>
          <div className="mt-3 max-h-[560px] space-y-2 overflow-y-auto">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => setSelectedAccountId(account.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left ${
                  selectedAccountId === account.id
                    ? 'border-neon-orange bg-orange-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="font-medium text-gray-900">{account.clientName}</div>
                <div className="text-xs text-gray-500">{account.serviceName || 'Service'}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 border-t pt-3">
            <label className="mb-2 block text-sm font-medium text-gray-700">Ajouter depuis abonnement</label>
            <select
              defaultValue=""
              onChange={(event) => {
                const subscription = subscriptions.find((entry) => entry.id === event.target.value);
                if (subscription) addFromSubscription(subscription);
                event.currentTarget.value = '';
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="">Selectionner un abonnement</option>
              {subscriptions.map((subscription) => (
                <option key={subscription.id} value={subscription.id}>
                  {subscription.subscriptionNumber} · {subscription.clientName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          {!selectedAccount ? (
            <p className="text-sm text-gray-500">Selectionne un compte pub pour le gerer.</p>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Client</label>
                  <input
                    value={selectedAccount.clientName}
                    onChange={(event) => patchSelected({ clientName: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email client</label>
                  <input
                    value={selectedAccount.clientEmail}
                    onChange={(event) => patchSelected({ clientEmail: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Offre mensuelle</label>
                  <input
                    type="number"
                    value={selectedAccount.monthlyRevenue}
                    onChange={(event) => patchSelected({ monthlyRevenue: Number(event.target.value || 0) })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Budget cible pub</label>
                  <input
                    type="number"
                    value={selectedAccount.budgetTarget}
                    onChange={(event) => patchSelected({ budgetTarget: Number(event.target.value || 0) })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Google Ads Customer ID</label>
                  <input
                    value={selectedAccount.googleAdsCustomerId}
                    onChange={(event) => patchSelected({ googleAdsCustomerId: event.target.value })}
                    placeholder="ex: 123-456-7890"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
              </div>
              {Array.isArray(selectedAccount.campaigns) && selectedAccount.campaigns.length > 0 && (
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900">Campagnes synchronisees</h4>
                  <div className="mt-3 space-y-2">
                    {selectedAccount.campaigns.slice(0, 8).map((campaign) => (
                      <div key={campaign.campaignId} className="grid gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm md:grid-cols-[minmax(0,1fr)_130px_90px_120px]">
                        <div className="font-medium text-gray-900">{campaign.campaignName}</div>
                        <div className="text-gray-600">{campaign.campaignStatus}</div>
                        <div className="text-gray-600">{campaign.clicks} clics</div>
                        <div className="font-semibold text-amber-700">
                          {currencyService.formatCurrencyAmount(campaign.cost, selectedAccount.currencyCode || 'CAD', {
                            display: 'code',
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">Depenses pub (journalieres)</h4>
                  <button
                    type="button"
                    onClick={() =>
                      patchSelected({
                        spends: [...selectedAccount.spends, emptySpend()],
                      })
                    }
                    className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700"
                  >
                    + Ajouter depense
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedAccount.spends.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucune depense enregistree.</p>
                  ) : (
                    selectedAccount.spends.map((spend) => (
                      <div key={spend.id} className="grid gap-2 md:grid-cols-[150px_120px_minmax(0,1fr)_42px]">
                        <input
                          type="date"
                          value={spend.date}
                          onChange={(event) =>
                            patchSelected({
                              spends: selectedAccount.spends.map((entry) =>
                                entry.id === spend.id ? { ...entry, date: event.target.value } : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <input
                          type="number"
                          value={spend.amount}
                          onChange={(event) =>
                            patchSelected({
                              spends: selectedAccount.spends.map((entry) =>
                                entry.id === spend.id
                                  ? { ...entry, amount: Number(event.target.value || 0) }
                                  : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <input
                          value={spend.campaignName}
                          onChange={(event) =>
                            patchSelected({
                              spends: selectedAccount.spends.map((entry) =>
                                entry.id === spend.id
                                  ? { ...entry, campaignName: event.target.value }
                                  : entry
                              ),
                            })
                          }
                          placeholder="Nom campagne Google Ads"
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            patchSelected({
                              spends: selectedAccount.spends.filter((entry) => entry.id !== spend.id),
                            })
                          }
                          className="rounded-lg border border-red-200 text-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">Calendrier pause / reprise pub</h4>
                  <button
                    type="button"
                    onClick={() =>
                      patchSelected({
                        pauses: [...selectedAccount.pauses, emptyPause()],
                      })
                    }
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800"
                  >
                    + Ajouter pause
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedAccount.pauses.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucune pause planifiee.</p>
                  ) : (
                    selectedAccount.pauses.map((pause) => (
                      <div key={pause.id} className="grid gap-2 md:grid-cols-[130px_130px_130px_minmax(0,1fr)_110px_42px]">
                        <input
                          type="date"
                          value={pause.startDate}
                          onChange={(event) =>
                            patchSelected({
                              pauses: selectedAccount.pauses.map((entry) =>
                                entry.id === pause.id ? { ...entry, startDate: event.target.value } : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <input
                          type="date"
                          value={pause.endDate}
                          onChange={(event) =>
                            patchSelected({
                              pauses: selectedAccount.pauses.map((entry) =>
                                entry.id === pause.id ? { ...entry, endDate: event.target.value } : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <input
                          type="date"
                          value={pause.resumeDate}
                          onChange={(event) =>
                            patchSelected({
                              pauses: selectedAccount.pauses.map((entry) =>
                                entry.id === pause.id ? { ...entry, resumeDate: event.target.value } : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <input
                          value={pause.reason}
                          onChange={(event) =>
                            patchSelected({
                              pauses: selectedAccount.pauses.map((entry) =>
                                entry.id === pause.id ? { ...entry, reason: event.target.value } : entry
                              ),
                            })
                          }
                          placeholder="Ex: Pause budget, attente validation client"
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <select
                          value={pause.status}
                          onChange={(event) =>
                            patchSelected({
                              pauses: selectedAccount.pauses.map((entry) =>
                                entry.id === pause.id
                                  ? { ...entry, status: event.target.value as MediaPauseEntry['status'] }
                                  : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        >
                          <option value="planned">Planifie</option>
                          <option value="done">Traite</option>
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            patchSelected({
                              pauses: selectedAccount.pauses.filter((entry) => entry.id !== pause.id),
                            })
                          }
                          className="rounded-lg border border-red-200 text-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MediaBuyingManager;
